import { useState } from 'react';
import { BACKEND_URL, type RunArtifact } from '@/lib/backend';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { AlertCircle, ChevronDown } from 'lucide-react';

/* ── Status badge ── */

export function StatusBadge({ status }: { status?: string }) {
  const variant =
    status === 'ok' || status === 'succeeded'
      ? 'default'
      : status === 'running' || status === 'queued'
        ? 'secondary'
        : 'destructive';
  return <Badge variant={variant}>{status ?? 'unknown'}</Badge>;
}

/* ── JSON block ── */

export function JsonBlock({ data }: { data: unknown }) {
  return (
    <pre className="bg-muted/50 rounded-md p-3 text-xs overflow-auto max-h-64 text-foreground font-mono">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}

/* ── Error box with CORS hint ── */

export function ErrorBox({ error }: { error: unknown }) {
  const msg = error instanceof Error ? error.message : String(error);
  const isCors =
    msg.toLowerCase().includes('failed to fetch') ||
    msg.toLowerCase().includes('network error');
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 text-destructive text-sm">
        <AlertCircle className="w-4 h-4 shrink-0" />
        <span className="break-all">{msg}</span>
      </div>
      {isCors && (
        <p className="text-xs text-muted-foreground pl-6">
          This looks like a CORS or network connectivity issue. Ensure the backend at{' '}
          <code className="bg-muted px-1 rounded">{BACKEND_URL}</code> is running and allows
          requests from this origin.
        </p>
      )}
    </div>
  );
}

/* ── Timestamp ── */

export function Timestamp({ value }: { value?: string }) {
  if (!value) return <span className="text-muted-foreground">—</span>;
  return <span>{new Date(value).toLocaleString()}</span>;
}

/* ── Artifact item ── */

export function ArtifactItem({ artifact }: { artifact: RunArtifact }) {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="border border-border rounded-md p-2 text-xs space-y-1">
        <CollapsibleTrigger asChild>
          <button className="flex items-center justify-between w-full text-left gap-2">
            <div className="flex items-center gap-2">
              <Badge variant="outline">{artifact.artifact_type}</Badge>
              {artifact.label && <span className="text-foreground">{artifact.label}</span>}
            </div>
            <div className="flex items-center gap-2">
              {artifact.created_at && (
                <span className="text-muted-foreground">
                  {new Date(artifact.created_at).toLocaleTimeString()}
                </span>
              )}
              <ChevronDown
                className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`}
              />
            </div>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          {artifact.content_json ? (
            <JsonBlock data={artifact.content_json} />
          ) : (
            <p className="text-muted-foreground">No content</p>
          )}
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

/* ── Collapsible payload section ── */

export function PayloadSection({
  label,
  data,
}: {
  label: string;
  data?: Record<string, unknown> | null;
}) {
  const [open, setOpen] = useState(false);
  const isEmpty = !data || Object.keys(data).length === 0;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button className="flex items-center justify-between w-full text-left gap-2 py-1.5 px-2 rounded hover:bg-muted/40 transition-colors">
          <span className="text-sm font-medium text-foreground">{label}</span>
          <div className="flex items-center gap-2">
            {isEmpty && <span className="text-xs text-muted-foreground">empty</span>}
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
            />
          </div>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-1">
        {isEmpty ? (
          <p className="text-xs text-muted-foreground pl-2">No data</p>
        ) : (
          <JsonBlock data={data} />
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
